/* eslint-disable max-len */
import parameterize from '../../../utils/parameterize'
import getEpisodeUrl from '../../../utils/getEpisodeUrl'
import getImageRenditions from '../../../utils/getImageRenditions'

export default async function getTabs({
  programmeData,
  programmeGuid,
  playerApi,
}) {
  const audioDescribed = programmeData?.accessibility?.audioDescribed
  const visuallySigned = programmeData?.accessibility?.visuallySigned
  const hasEpisodes = Boolean(programmeData?.availableEpisodes)
  const hasSeries = hasEpisodes && ((Boolean(programmeData?.series) && programmeData?.series?.length !== 0))
  const hasShortformCollections = Boolean(programmeData?.shortform)

  const defaultEpisodeQuery = {
    params: {
      path: '/episodes',
      query: {
        limit: 100,
        offset: 0,
        orderBy: '-schedule.startTime,-availability.from,-createdAt',
        'programme.guid': programmeGuid,
        groupToken: '0071',
      },
    },
  }

  let primaryTabs = []
  let secondaryTabs = []
  const accessibleTabs = {
    ad: [],
    vs: [],
  }

  if (hasSeries) {
    primaryTabs = programmeData?.series.map(item => buildTab({
      name: item.name,
      programmeGuid,
      type: 'episode',
      params: {
        path: '/episodes',
        query: {
          'series.guid': item.guid,
          limit: 100,
          groupToken: '0071',
        },
      },
    }))
  } else if (hasEpisodes) {
    primaryTabs.splice(0, 0, buildTab({
      name: 'Episodes',
      programmeGuid,
      type: 'episode',
      ...defaultEpisodeQuery,
    }))
  }

  if (audioDescribed || visuallySigned) {
    ['ad', 'vs'].forEach((type) => {
      if (hasSeries) {
        const seriesData = programmeData?.accessibility[`${type}Data`]?.series
        if (!seriesData) return

        const tab = seriesData.map(item => buildTab({
          name: item.name,
          programmeGuid: `${programmeGuid}-${type}`,
          type: 'episode',
          params: {
            path: '/episodes',
            query: {
              'series.guid': `${item.guid}`,
              limit: 100,
              groupToken: '0071',
            },
          },
          accessibility: type,
        }))
        accessibleTabs[type] = tab
      } else if (hasEpisodes) {
        const tab = buildTab({
          name: `${type === 'ad' ? 'Audio Described' : 'Visually Signed'} Episodes`,
          programmeGuid: `${programmeGuid}-${type}`,
          type: 'episode',
          params: {
            path: '/episodes',
            query: {
              limit: 100,
              offset: 0,
              orderBy: '-schedule.startTime,-availability.from,-createdAt',
              'programme.guid': `${programmeGuid}-${type}`,
              groupToken: '0071',
            },
          },
          accessibility: type,
        })
        accessibleTabs[type] = [tab]
      }
    })
  }

  if (hasShortformCollections) {
    secondaryTabs = programmeData?.shortform.map(item => buildTab({
      name: item.name,
      programmeGuid,
      type: 'video',
      params: {
        path: '/shortform',
        query: {
          'collection.guid': item.guid,
          limit: 100,
          orderBy: 'index,-createdAt',
          groupToken: '0071',
        },
      },
    }))
  }

  const tabs = [...primaryTabs, ...secondaryTabs, ...accessibleTabs.ad, ...accessibleTabs.vs]

  if (!tabs.length) return []
  // we can't tell which tab is selected until the page loads
  // so always render the first tab server-side
  const { results, reason } = await playerApi(tabs[0].params)
  if (reason) return tabs
  tabs[0].data = results.map(item => remapCardContent({
    item,
    type: tabs[0].type,
  }))

  return tabs
}

function buildTab({
  programmeGuid,
  name,
  params,
  type,
  accessibility = null,
}) {
  return {
    title: name,
    id: `${programmeGuid}-${parameterize(name)}`,
    params,
    type,
    accessibility,
  }
}

export const remapCardContent = ({ item, type }) => {
  const link = getEpisodeUrl({
    episodeId: item.id,
    programmeGuid: item?.programme?.guid,
    path: type,
  })

  return {
    id: item?.id,
    link,
    summary: item?.summary,
    title: item?.title,
    availableUntil: item?.availability?.until,
    image: getImageRenditions(
      item?.images?.[0]._filepath,
      ['225x127', '365x205', '460x259']
    ),
    duration: item?.video?._duration,
    hasSubtitles: Boolean(item._subtitles),
    hasGuidanceText: Boolean(item.guidanceText),
    hasInfoText: Boolean(item.informationText),
    forceAds: Boolean(item.forceAds),
    premierSportsExclusive: item?.premierSportsExclusive,
  }
}
